import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BellIcon, ZapIcon, SmartphoneIcon, EyeIcon } from 'lucide-react';
const features = [{
  icon: EyeIcon,
  text: 'See who owes what — instantly'
}, {
  icon: BellIcon,
  text: 'Get notified the moment money moves'
}, {
  icon: ZapIcon,
  text: 'Close the kitty — no one owes anyone'
}, {
  icon: SmartphoneIcon,
  text: 'Deposit funds with Apple Pay, Google Pay, or card'
}];
const transactions = [{
  title: 'Beers',
  person: 'John Smith',
  amount: -68.8,
  perPerson: 34.4,
  date: '12/04/2026, 07:19 PM',
  type: 'Expense',
  avatar: 'https://i.pravatar.cc/100?img=12'
}, {
  title: 'Food',
  person: 'John Smith',
  amount: -45.8,
  perPerson: 22.9,
  date: '12/04/2026, 07:18 PM',
  type: 'Expense',
  avatar: 'https://i.pravatar.cc/100?img=12'
}, {
  title: 'Deposit',
  person: 'John Smith',
  amount: 2250.0,
  perPerson: null,
  date: '12/04/2026, 03:46 PM',
  type: 'Deposit',
  avatar: 'https://i.pravatar.cc/100?img=12'
}, {
  title: 'Deposit',
  person: 'John Smith',
  amount: 100.0,
  perPerson: null,
  date: '12/04/2026, 09:35 AM',
  type: 'Deposit',
  avatar: 'https://i.pravatar.cc/100?img=12'
}, {
  title: 'Test',
  person: 'Ben',
  amount: -40.0,
  perPerson: 20.0,
  date: '12/04/2026, 09:34 AM',
  type: 'Expense',
  avatar: 'https://i.pravatar.cc/100?img=5'
}];
export function ValueProposition() {
  const [activeTab, setActiveTab] = useState<'settlement' | 'history'>('settlement');
  return <section className="py-20 lg:py-28 bg-kitty-yellow-50/50 overflow-hidden" data-id="element-287">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-288">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-289">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-290">
            <p className="text-sm font-semibold text-kitty-yellow-600 uppercase tracking-widest mb-3" data-id="element-291">
              Full transparency
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-kitty-dark mb-6 leading-tight tracking-tight" data-id="element-292">
              Every penny.
              <br data-id="element-293" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-kitty-yellow-600 to-orange-500" data-id="element-294">
                Accounted for.
              </span>
            </h2>
            <p className="text-lg text-gray-500 mb-8 max-w-md" data-id="element-295">
              Everyone sees the pot balance and where the money goes. No
              guessing. No awkward questions.
            </p>

            <ul className="space-y-4" data-id="element-296">
              {features.map((feature, index) => <motion.li key={index} initial={{
              opacity: 0,
              x: -10
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.2 + index * 0.08
            }} className="flex items-center gap-3" data-id="element-297">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-kitty-yellow/20 flex items-center justify-center" data-id="element-298">
                    <feature.icon className="w-4 h-4 text-kitty-yellow-700" data-id="element-299" />
                  </div>
                  <span className="text-gray-800 font-medium text-lg" data-id="element-300">
                    {feature.text}
                  </span>
                </motion.li>)}
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative" data-id="element-301">
            {/* App-like card mockup */}
            <div className="bg-white rounded-[2rem] p-6 shadow-card border border-gray-100 relative z-10 overflow-hidden" data-id="element-302">
              {/* Kitty Balance Banner */}
              <div className="bg-kitty-yellow rounded-2xl px-6 py-4 flex justify-between items-center mb-4" data-id="element-303">
                <span className="font-bold text-kitty-dark text-base" data-id="element-304">
                  Kitty Balance
                </span>
                <span className="font-extrabold text-kitty-dark text-xl" data-id="element-305">
                  £2,450.00
                </span>
              </div>

              {/* Tabs */}
              <div className="bg-kitty-yellow-100 rounded-xl p-1 flex mb-6" data-id="element-306">
                <button onClick={() => setActiveTab('settlement')} className={`flex-1 rounded-lg py-2.5 text-center transition-all duration-200 ${activeTab === 'settlement' ? 'bg-kitty-yellow' : ''}`} data-id="element-307">
                  <span className={`text-sm ${activeTab === 'settlement' ? 'font-bold text-kitty-dark' : 'font-medium text-kitty-dark/50'}`} data-id="element-308">
                    Who owes what
                  </span>
                </button>
                <button onClick={() => setActiveTab('history')} className={`flex-1 rounded-lg py-2.5 text-center transition-all duration-200 ${activeTab === 'history' ? 'bg-kitty-yellow' : ''}`} data-id="element-309">
                  <span className={`text-sm ${activeTab === 'history' ? 'font-bold text-kitty-dark' : 'font-medium text-kitty-dark/50'}`} data-id="element-310">
                    History
                  </span>
                </button>
              </div>

              <AnimatePresence mode="wait" data-id="element-311">
                {activeTab === 'settlement' ? <motion.div key="settlement" initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-312">
                    {/* Settled message */}
                    <div className="text-center mb-6" data-id="element-313">
                      <p className="font-bold text-kitty-dark text-base" data-id="element-314">
                        Everyone is settled up 👍
                      </p>
                      <p className="text-gray-400 text-sm mt-1" data-id="element-315">
                        Any new expenses will appear here
                      </p>
                    </div>

                    {/* You'll get back */}
                    <div className="mb-5" data-id="element-316">
                      <p className="font-bold text-kitty-dark text-sm mb-3" data-id="element-317">
                        You'll get back
                      </p>
                      <div className="border border-gray-200 rounded-2xl px-4 py-3.5 flex items-center gap-3" data-id="element-318">
                        <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0" data-id="element-319">
                          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-320">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-321" />
                          </svg>
                        </div>
                        <span className="font-bold text-kitty-dark text-base flex-1" data-id="element-322">
                          You
                        </span>
                        <span className="font-bold text-green-600 text-base bg-green-50 px-3 py-1 rounded-lg" data-id="element-323">
                          £30
                        </span>
                      </div>
                    </div>

                    {/* Needs to add */}
                    <div data-id="element-324">
                      <p className="font-bold text-kitty-dark text-sm mb-3" data-id="element-325">
                        Needs to add
                      </p>
                      <div className="space-y-3" data-id="element-326">
                        <div className="border border-gray-200 rounded-2xl px-4 py-3.5 flex items-center gap-3" data-id="element-327">
                          <img src="https://i.pravatar.cc/100?img=12" alt="John Smith" className="w-10 h-10 rounded-full border-2 border-white shadow-sm flex-shrink-0" data-id="element-328" />
                          <span className="font-bold text-kitty-dark text-base flex-1" data-id="element-329">
                            John Smith
                          </span>
                          <span className="font-bold text-red-500 text-base bg-red-50 px-3 py-1 rounded-lg" data-id="element-330">
                            £20
                          </span>
                        </div>
                        <div className="border border-gray-200 rounded-2xl px-4 py-3.5 flex items-center gap-3" data-id="element-331">
                          <img src="https://i.pravatar.cc/100?img=5" alt="Sarah" className="w-10 h-10 rounded-full border-2 border-white shadow-sm flex-shrink-0" data-id="element-332" />
                          <span className="font-bold text-kitty-dark text-base flex-1" data-id="element-333">
                            Sarah
                          </span>
                          <span className="font-bold text-red-500 text-base bg-red-50 px-3 py-1 rounded-lg" data-id="element-334">
                            £10
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div> : <motion.div key="history" initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: 10
              }} transition={{
                duration: 0.2
              }} className="space-y-3" data-id="element-335">
                    {transactions.map((tx, index) => <div key={index} className="border border-gray-200 rounded-2xl px-4 py-3.5 flex items-center gap-3" data-id="element-336">
                        <img src={tx.avatar} alt={tx.person} className="w-10 h-10 rounded-full border-2 border-white shadow-sm flex-shrink-0" data-id="element-337" />
                        <div className="flex-1 min-w-0" data-id="element-338">
                          <p className="font-bold text-kitty-dark text-sm leading-tight" data-id="element-339">
                            {tx.title}
                          </p>
                          <p className="text-gray-400 text-xs mt-0.5 truncate" data-id="element-340">
                            {tx.person}
                            {tx.perPerson !== null && ` • £${tx.perPerson.toFixed(2)}`}
                          </p>
                          <p className="text-gray-400 text-xs" data-id="element-341">{tx.date}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0" data-id="element-342">
                          {tx.type === 'Expense' && <span className="text-xs font-medium text-kitty-yellow-700 bg-kitty-yellow-100 px-2 py-0.5 rounded-md" data-id="element-343">
                              Expense
                            </span>}
                          <span className={`font-bold text-sm px-2.5 py-0.5 rounded-lg ${tx.amount < 0 ? 'text-kitty-yellow-700 bg-kitty-yellow-50' : 'text-green-600 bg-green-50'}`} data-id="element-344">
                            {tx.amount < 0 ? `-£${Math.abs(tx.amount).toFixed(2)}` : `+£${tx.amount.toFixed(2)}`}
                          </span>
                        </div>
                      </div>)}
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Floating notification — outside card to avoid overflow-hidden clipping */}

            {/* Decorative blobs */}
            <div className="absolute top-10 -right-10 w-32 h-32 bg-kitty-yellow-200 rounded-full mix-blend-multiply filter blur-2xl opacity-60" data-id="element-345" />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-orange-200 rounded-full mix-blend-multiply filter blur-2xl opacity-60" data-id="element-346" />
          </motion.div>
        </div>
      </div>
    </section>;
}